import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import {
  BookOpen, FileText, BookMarked, FlaskConical,
  ChevronLeft, Eye
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Helper to build viewer URL  
function getViewerUrl(fileUrl, title) {
  return `/notes/view?file=${encodeURIComponent(fileUrl)}&title=${encodeURIComponent(title || 'Notes')}`;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const resourceSections = [
  { key: "notes", label: "Notes", icon: BookOpen, color: "from-cyan-500 to-blue-600" },
  { key: "pyq", label: "Previous Year Papers", icon: FileText, color: "from-violet-500 to-purple-600" },
  { key: "syllabus", label: "Syllabus", icon: BookMarked, color: "from-pink-500 to-rose-600" },
  { key: "lab_manual", label: "Lab Manuals", icon: FlaskConical, color: "from-emerald-500 to-green-600" },
];

export default function SubjectPage() {
  const { subjectId } = useParams();
  const [subject, setSubject] = useState(null);
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/subject/${subjectId}/resources`)
      .then((r) => {
        setSubject(r.data.subject);
        setResources(r.data.resources);
      })
      .catch(() => toast.error("Failed to load subject resources"))
      .finally(() => setLoading(false));
  }, [subjectId]);

  if (loading) {
    return <div className="min-h-screen pt-24 text-center text-zinc-500">Loading...</div>;
  }

  if (!subject) {
    return (
      <div className="min-h-screen pt-24 text-center">
        <div className="text-zinc-500 mb-4">Subject not found</div>
        <Link to="/" className="text-violet-400 hover:text-violet-300">Go back home</Link>
      </div>
    );
  }

  return (
    <div data-testid="subject-page" className="min-h-screen pt-24 pb-12">
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-violet-600/5 to-transparent pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Breadcrumb */}
          <motion.div variants={fadeInUp} className="mb-6">
            <Link 
              to="/"
              data-testid="subject-back-btn" 
              className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />Back to Home
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center gap-5 mb-10">
            <div className="shrink-0 w-16 h-16 rounded-xl bg-violet-600/10 border border-white/10 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-violet-400" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight text-white">{subject.name}</h1>
              <p className="text-sm text-zinc-500 mt-1">
                {subject.branch} &middot; Semester {subject.semester} &middot; {subject.code}
              </p>
            </div>
          </motion.div>

          {/* Resource Sections */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {resourceSections.map((section) => {
              const items = resources?.[section.key] || [];
              return (
                <motion.div key={section.key} variants={fadeInUp}
                  data-testid={`resource-section-${section.key}`}
                  className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md overflow-hidden">
                  <div className="p-5 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <div className={`rounded-lg bg-gradient-to-br ${section.color} p-2.5`}>
                        <section.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-semibold text-white">{section.label}</h3>
                        <p className="text-xs text-zinc-600">{items.length} resource{items.length !== 1 ? "s" : ""}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    {items.length > 0 ? (
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div key={item.id} data-testid={`resource-item-${item.id}`}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                            <div className="flex-1 min-w-0 mr-3">
                              <a 
                                href={getViewerUrl(item.file_url, item.title)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-zinc-300 hover:text-violet-300 truncate block transition-colors"
                              >
                                {item.title}
                              </a>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <a 
                                href={getViewerUrl(item.file_url, item.title)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`preview-btn-${item.id}`}
                                className="p-1.5 rounded-md hover:bg-white/10 transition-colors" 
                                title="View"
                              >
                                <Eye className="w-4 h-4 text-zinc-500 hover:text-cyan-400" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-sm text-zinc-600">No {section.label.toLowerCase()} yet</p>
                        <p className="text-xs text-zinc-700 mt-1">Resources will appear here once uploaded</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
